import * as React from "react";
import { Input, TextArea } from "@progress/kendo-react-inputs";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { Label } from "@progress/kendo-react-labels";
import { Grid, GridColumn as Column, GridToolbar } from "@progress/kendo-react-grid";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { AmountFormat, formatDateForAPI } from "components/common/formatter";
import { SvgIcon } from "@progress/kendo-react-common";
import { downloadIcon } from "@progress/kendo-svg-icons";
import { CrossIcon } from "components/tabs/TabTitle";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import {
  EditJournalTemplateDialog,
  LoadJournalTemplatesDialog,
  SaveJournalTemplateDialog,
} from "./JournalTemplateDialogs";
import {
  createInitialJournalGridData,
  JournalAmountCell,
  JournalCommandCell,
  JournalNominalCell,
  JournalNominalNameCell,
  JournalNominalTypeCell,
  removeJournalRow,
} from "./JournalGridCells";
import { Button } from "@progress/kendo-react-buttons";
import { createJournalPosting } from "services/postingsService";
import { useNonControlNominals } from "services/nominalsService";
import {
  createJournalTemplate,
  deactivateJournalTemplate,
  getJournalTemplateById,
  getJournalTemplates,
  importJournalTemplate,
  updateJournalTemplate,
} from "services/journalTemplatesService";
import { toast } from "react-toastify";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { TransactionPeriod } from "components/common/TransactionPeriod";
import { getSelectedSessionPeriodNumber } from "services/transactionPeriodService";
import { v4 as uuidV4 } from "uuid";
import SubmitButton from "components/common/SubmitButton";
import styles from "./JournalPosting.module.scss";

const JournalPostings = () => {
  const [credits, setCredits] = useState(0);
  const [debits, setDebits] = useState(0);
  const [tableError, setTableError] = useState(false);
  const [isBalanced, setIsBalanced] = useState(false);
  const date = new Date();
  const [localData, setData] = useState(() => createInitialJournalGridData());
  const [reference, setReference] = useState("");
  const [dateselector, setdateselector] = useState(date);
  const [details, setDetails] = useState("");
  const [resetKey, setResetKey] = useState(uuidV4());
  const [validationError, setValidationError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showLoadTemplateDialog, setShowLoadTemplateDialog] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showEditTemplateDialog, setShowEditTemplateDialog] = useState(false);
  const [editingTemplateName, setEditingTemplateName] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [updatingTemplate, setUpdatingTemplate] = useState(false);
  const importFileInputRef = useRef(null);
  const [importingTemplate, setImportingTemplate] = useState(false);
  const excelExportRef = useRef(null);
  const { data: allNominals } = useNonControlNominals();
  const nominals = allNominals || [];

  const remove = useCallback((dataItem) => {
    setData((prev) => {
      const next = removeJournalRow(prev, dataItem);
      setTableError(next.length < 2);
      return next;
    });
  }, []);

  const noAmountsEntered = useMemo(() => {
    if (!validationError) return false;
    return localData.every((item) => {
      const credit = Number(item.CreditAmount) || 0;
      const debit = Number(item.DebitAmount) || 0;
      return credit === 0 && debit === 0;
    });
  }, [localData, validationError]);

  // Memoize Grid cell renderers to prevent editor remount/focus loss while typing.
  const CommandCell = useCallback(
    (props) => <JournalCommandCell {...props} remove={remove} />,
    [remove],
  );
  const NominalCell = useCallback(
    (props) => (
      <JournalNominalCell
        {...props}
        nominals={nominals}
        showValidation={validationError}
        noAmountsEntered={noAmountsEntered}
      />
    ),
    [nominals, validationError, noAmountsEntered],
  );
  const AmountCell = useCallback(
    (props) => (
      <JournalAmountCell
        {...props}
        showValidation={validationError}
        noAmountsEntered={noAmountsEntered}
      />
    ),
    [validationError, noAmountsEntered],
  );
  const NominalNameCell = (props) => (
    <JournalNominalNameCell {...props} nominals={nominals} />
  );
  const NominalTypeCell = (props) => (
    <JournalNominalTypeCell {...props} nominals={nominals} />
  );

  const itemChange = (event) => {
    const field = event.field || "";
    if (field === "CreditAmount" || field === "DebitAmount") {
      const numValue = parseFloat(event.value);
      const value = Number.isFinite(numValue) ? numValue : 0;
      setData((prev) =>
        prev.map((item) => {
          if (item.journalLineId !== event.dataItem.journalLineId) return item;
          if (field === "CreditAmount") {
            return {
              ...item,
              CreditAmount: value,
              DebitAmount:
                typeof value === "number" && value !== 0 ? 0 : item.DebitAmount,
            };
          }
          return {
            ...item,
            DebitAmount: value,
            CreditAmount:
              typeof value === "number" && value !== 0 ? 0 : item.CreditAmount,
          };
        }),
      );
    } else {
      setData((prev) =>
        prev.map((item) =>
          item.journalLineId === event.dataItem.journalLineId
            ? { ...item, [field]: event.value }
            : item,
        ),
      );
    }
  };

  const generateJournalLineId = (data) =>
    data.reduce((acc, current) => Math.max(acc, current.journalLineId), 0) + 1;

  const addNew = () => {
    const id = generateJournalLineId(localData);
    const newDataItem = {
      journalLineId: id,
      NominalInfo: {
        nominalRef: null,
      },
      CreditAmount: 0,
      DebitAmount: 0,
    };
    setTableError(localData.length + 1 < 2);
    setData([...localData, newDataItem]);
  };

  const removeAllRows = () => {
    setData(createInitialJournalGridData());
    setResetKey(uuidV4());
    setTableError(false);
  };
  const calculateCreditDebit = () => {
    const sumCredit = localData.reduce(
      (sum, item) => sum + (Number(item.CreditAmount) || 0),
      0,
    );
    const sumDebit = localData.reduce(
      (sum, item) => sum + (Number(item.DebitAmount) || 0),
      0,
    );
    setCredits(sumCredit);
    setDebits(sumDebit);
    setIsBalanced(sumCredit === sumDebit);
  };

  useEffect(() => {
    calculateCreditDebit();
  }, [localData]);

  const isPostDisabled = !isBalanced;

  const handleSubmit = async (e) => {
    e.preventDefault();
    let cAmount = 0;
    let dAmount = 0;
    const data = [
      {
        postingType: "JNL",
        date: formatDateForAPI(dateselector),
        reference: reference,
        details: details,
        transactionPeriod: getSelectedSessionPeriodNumber(),
        journalEntries: [],
      },
    ];

    if (localData.length < 2) {
      setTableError(true);
      return;
    }
    if (reference === "" || details === "") {
      setValidationError(true);
      return;
    }

    const rowsWithAmount = localData.filter((item) => {
      const credit = Number(item.CreditAmount) || 0;
      const debit = Number(item.DebitAmount) || 0;
      return credit !== 0 || debit !== 0;
    });

    if (rowsWithAmount.length === 0) {
      setValidationError(true);
      return;
    }

    if (rowsWithAmount.some((item) => !item.NominalInfo?.nominalRef)) {
      setValidationError(true);
      return;
    }

    rowsWithAmount.forEach((item) => {
      const credit = Number(item.CreditAmount) || 0;
      const debit = Number(item.DebitAmount) || 0;
      cAmount += credit;
      dAmount += debit;
      data[0].journalEntries.push({
        nominalRef: item.NominalInfo?.nominalRef,
        amount: credit === 0 || item.CreditAmount === null ? -debit : credit,
      });
    });

    if (cAmount !== dAmount) {
      return toast.error("Credit and Debit Amounts Do Not Match", {
        style: {
          marginRight: "10px",
        },
      });
    }

    if (data[0]?.journalEntries?.length > 0) {
      try {
        setLoading(true);
        const response = await createJournalPosting(data);
        const postingData =
          response.data?.data?.[0] || response.data?.data?.data?.[0];

        if (postingData?.transactionNo) {
          toast.success(
            `Posted Successfully - ${postingData.postingType} - ${postingData.transactionNo}`,
          );
          // Reset form after successful posting
          setData(createInitialJournalGridData());
          setDetails("");
          setReference("");
          setdateselector(new Date(Date.now()));
          setResetKey(uuidV4());
          setValidationError(false);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
  };

  const buildJournalTemplateData = () =>
    localData
      .map((item) => {
        const credit = Number(item.CreditAmount) || 0;
        const debit = Number(item.DebitAmount) || 0;
        if (!item.NominalInfo?.nominalRef || credit + debit === 0) {
          return null;
        }
        return {
          journalTemplateDataId: null,
          nominalRef: item.NominalInfo.nominalRef,
          amount: credit === 0 || item.CreditAmount === null ? -debit : credit,
          costCentre: "",
          postingDetails: "",
          active: true,
        };
      })
      .filter(Boolean);

  const mapTemplateToGridRows = (template) => {
    const lines = template?.journalTemplateData || [];
    return lines.map((line, idx) => {
      const amount = Number(line.amount) || 0;
      return {
        journalLineId: idx + 1,
        NominalInfo: { nominalRef: line.nominalRef || null },
        CreditAmount: amount > 0 ? amount : 0,
        DebitAmount: amount < 0 ? Math.abs(amount) : 0,
      };
    });
  };

  /** Flat rows + ExcelExportColumn definitions (Grid column fields are not compatible: objects export as empty per Kendo OOXML). */
  const exportRows = useMemo(
    () =>
      (localData || []).map((row) => {
        const typeName = row?.NominalInfo?.typeName || "";
        const subTypeName = row?.NominalInfo?.subTypeName || "";
        const typeDisplay = [typeName, subTypeName].filter(Boolean).join(" ");
        return {
          nominalRef: row?.NominalInfo?.nominalRef ?? "",
          nominalName: row?.NominalInfo?.nominalName ?? "",
          type: typeDisplay,
          creditAmount: Number(row?.CreditAmount) || 0,
          debitAmount: Number(row?.DebitAmount) || 0,
        };
      }),
    [localData],
  );

  const handleExportExcel = () => {
    excelExportRef.current?.save();
  };

  const openSaveTemplateDialog = () => {
    setTemplateName("");
    setShowSaveTemplateDialog(true);
  };

  const closeSaveTemplateDialog = () => {
    if (savingTemplate) return;
    setShowSaveTemplateDialog(false);
  };

  const handleSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      return;
    }

    const journalTemplateData = buildJournalTemplateData();
    if (!journalTemplateData.length) {
      toast.error("Please fill at least one journal entry with an amount.");
      return;
    }

    const payload = {
      templateName: name,
      reference,
      details,
      isRecurring: false,
      active: true,
      journalTemplateData,
      recurringFrequency: 0,
    };

    try {
      setSavingTemplate(true);
      await createJournalTemplate(payload);
      toast.success("Journal template saved.");
      setShowSaveTemplateDialog(false);
    } catch (error) {
    } finally {
      setSavingTemplate(false);
    }
  };

  const openLoadTemplateDialog = async () => {
    setShowLoadTemplateDialog(true);
    setSelectedTemplateId(null);
    setTemplatesLoading(true);
    try {
      const list = await getJournalTemplates();
      setTemplates((list || []).filter((t) => t?.active !== false));
    } catch (error) {
      // handled by API layer
    } finally {
      setTemplatesLoading(false);
    }
  };

  const closeLoadTemplateDialog = () => {
    if (templatesLoading || updatingTemplate) return;
    setShowLoadTemplateDialog(false);
  };

  const handleLoadSelectedTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      setTemplatesLoading(true);
      const template = await getJournalTemplateById(selectedTemplateId);
      if (!template) return;

      setReference(template.reference || "");
      setDetails(template.details || "");
      const rows = mapTemplateToGridRows(template);
      setData(rows.length ? rows : createInitialJournalGridData());
      setResetKey(uuidV4());
      setShowLoadTemplateDialog(false);
      setValidationError(false);
      setTableError(rows.length < 2);
    } catch (error) {
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openEditSelectedTemplate = () => {
    if (!selectedTemplateId) return;
    const selected = templates.find(
      (t) => t.journalTemplateId === selectedTemplateId,
    );
    setEditingTemplateId(selectedTemplateId);
    setEditingTemplateName(selected?.templateName || "");
    setShowEditTemplateDialog(true);
  };

  const closeEditTemplateDialog = () => {
    if (updatingTemplate) return;
    setShowEditTemplateDialog(false);
  };

  const handleUpdateTemplateName = async () => {
    const name = editingTemplateName.trim();
    if (!editingTemplateId) return;
    if (!name) {
      toast.error("Please enter a template name.");
      return;
    }
    try {
      setUpdatingTemplate(true);
      const existing = await getJournalTemplateById(editingTemplateId);
      if (!existing) return;
      await updateJournalTemplate(editingTemplateId, {
        ...existing,
        templateName: name,
      });
      const list = await getJournalTemplates();
      setTemplates((list || []).filter((t) => t?.active !== false));
      setShowEditTemplateDialog(false);
      toast.success("Journal template updated.");
    } catch (error) {
    } finally {
      setUpdatingTemplate(false);
    }
  };

  const handleDeleteSelectedTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      setUpdatingTemplate(true);
      await deactivateJournalTemplate(selectedTemplateId);
      const list = await getJournalTemplates();
      setTemplates((list || []).filter((t) => t?.active !== false));
      setSelectedTemplateId(null);
      toast.success("Journal template deleted.");
    } catch (error) {
    } finally {
      setUpdatingTemplate(false);
    }
  };

  const openImportFilePicker = () => {
    if (importingTemplate) return;
    importFileInputRef.current?.click();
  };

  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    try {
      setImportingTemplate(true);
      const template = await importJournalTemplate(file);
      if (!template) return;

      setReference(template.reference || "");
      setDetails(template.details || "");

      const rows = mapTemplateToGridRows(template);
      setData(rows.length ? rows : createInitialJournalGridData());
      setResetKey(uuidV4());
      setValidationError(false);
      setTableError(rows.length < 2);
      toast.success("Journal imported.");
    } catch (error) {
    } finally {
      setImportingTemplate(false);
    }
  };

  const onChangedateselect = (event) => {
    setdateselector(event.value);
  };

  return (
    <>
      <div className={`container-fluid ps-0 pt-0 bg ${styles.pageContainer}`}>
        <div className="row h-100 g-0 align-items-stretch">
          <div
            className={`bgForm ms-2 pt-4 d-flex justify-content-center align-item-center ${styles.sideForm}`}>
            <div
              style={{
                width: "85%",
              }}>
              <div className="mb-3">
                <Label className="mb-1">Date</Label>
                <div>
                  <DatePicker
                    className="datepicker"
                    value={dateselector}
                    toggleButton={CalenderCustomToggleButton}
                    onChange={onChangedateselect}
                  />
                </div>
              </div>
              <div className="mb-3">
                <Label className="mb-1">Reference</Label>
                <Input
                  width={300}
                  value={reference}
                  required
                  onChange={(e) => {
                    setReference(e.target.value);
                  }}
                  placeholder={"Enter Reference"}
                  maxLength={40}
                  style={{
                    border:
                      validationError && reference === "" ? "1px solid red" : "",
                  }}></Input>
              </div>
              <div className="mb-3">
                <Label className="mb-1">Details</Label>
                <TextArea
                  cols={3}
                  required
                  placeholder="Enter Details"
                  value={details}
                  style={{
                    maxWidth: "675px",
                    height: "65px",
                    border: validationError && details === "" ? "1px solid red" : "",
                  }}
                  onChange={(e) => {
                    setDetails(e.target.value);
                  }}
                  maxLength={200}
                />
              </div>
            </div>
          </div>
          <div
            className={`col h-100 ${styles.rightColumn}`}
            style={{ overflowX: "auto" }}>
            <div className="flex-shrink-0">
              <TransactionPeriod />
            </div>
            <div className={`ms-4 me-3 ${styles.gridArea}`}>
              <div className={styles.gridBody}>
                <ExcelExport
                  data={exportRows}
                  fileName={`Journal-${new Date().toISOString().slice(0, 10)}.xlsx`}
                  ref={excelExportRef}>
                  <ExcelExportColumn
                    field="nominalRef"
                    title="Nominal"
                    width={200}
                  />
                  <ExcelExportColumn
                    field="nominalName"
                    title="Nominal Name"
                    width={150}
                  />
                  <ExcelExportColumn field="type" title="Type" width={120} />
                  <ExcelExportColumn
                    field="creditAmount"
                    title="Credit Amount"
                    width={140}
                  />
                  <ExcelExportColumn
                    field="debitAmount"
                    title="Debit Amount"
                    width={140}
                  />
                </ExcelExport>
                <Grid
                  style={{ height: "100%", minWidth: 920 }}
                  data={localData}
                  onItemChange={itemChange}
                  dataItemKey={"journalLineId"}
                  key={resetKey}
                  className={`mt-4 ${styles.journalGridFlex}`}>
                  {tableError && (
                    <GridToolbar>
                      <i style={{ color: "red" }}>
                        Please fill at least two records
                      </i>
                    </GridToolbar>
                  )}
                  <Column
                    field="NominalInfo"
                    title="Nominal"
                    width="200px"
                    cell={NominalCell}
                    editable={true}
                    headerClassName="ps-3"
                  />
                  <Column
                    field="NominalInfo.nominalName"
                    cell={NominalNameCell}
                    title="Nominal Name"
                    width="150px"
                    editable={false}
                  />
                  <Column
                    field="NominalInfo.type"
                    title="Type"
                    editable={false}
                    cell={NominalTypeCell}
                    width="120px"
                  />
                  <Column
                    field="CreditAmount"
                    title="Credit Amount"
                    editor="text"
                    cell={AmountCell}
                    width="140px"
                  />
                  <Column
                    field="DebitAmount"
                    title="Debit Amount"
                    editor="text"
                    cell={AmountCell}
                    width="140px"
                  />
                  <Column
                    title=""
                    cell={CommandCell}
                    width="60px"
                    headerCell={() => (
                      <div title="Remove All">
                        <CrossIcon onClick={removeAllRows} />
                      </div>
                    )}
                  />
                </Grid>
              </div>
              <GridToolbar className={styles.footerToolbar}>
                <div className="d-flex justify-content-between align-items-center w-100 px-2">
                  <Button
                    title="Add new"
                    className="common-btn bgBtnSecondary"
                    onClick={addNew}>
                    Add new line
                  </Button>

                  <button
                    type="button"
                    className="btn p-0 border-0 d-flex align-items-center justify-content-center"
                    title="Download (Excel)"
                    onClick={handleExportExcel}>
                    <SvgIcon icon={downloadIcon} />
                  </button>
                </div>
              </GridToolbar>
              <div
                className={`my-4 d-flex justify-content-between align-items-end flex-wrap gap-2 ${styles.bottomActions}`}>
                <div className="d-flex flex-shrink-0">
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      className="common-btn "
                      type="button"
                      onClick={openSaveTemplateDialog}>
                      Save
                    </Button>
                    <Button
                      className="common-btn "
                      type="button"
                      onClick={openLoadTemplateDialog}>
                      Load
                    </Button>
                    <Button
                      className="common-btn "
                      type="button"
                      onClick={openImportFilePicker}
                      disabled={importingTemplate}>
                      Import
                    </Button>
                  </div>
                </div>

                <div className="d-flex flex-column align-items-end flex-shrink-0">
                  <div className="d-flex align-items-center">
                    <Label className="me-5">Credits</Label>
                    <Input
                      value={AmountFormat(credits)}
                      style={{ textAlign: "right", width: "180px" }}
                      readOnly></Input>
                  </div>

                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <Label className="me-5">Debits</Label>
                    <Input
                      value={AmountFormat(debits)}
                      style={{ textAlign: "right", width: "180px" }}
                      readOnly></Input>
                  </div>
                  {!isBalanced && (
                    <div className="text-danger small mt-2 text-end">
                      Difference: {AmountFormat(Math.abs(credits - debits))}
                    </div>
                  )}

                  <div className="buttonstyle mt-2 d-flex justify-content-end gap-2">
                    <SubmitButton
                      className="common-btn"
                      form="myForm"
                      type="submit"
                      isLoading={loading}
                      onClick={handleSubmit}
                      disabled={isPostDisabled}>
                      Post
                    </SubmitButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <input
        ref={importFileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleImportFileSelected}
      />

      <SaveJournalTemplateDialog
        show={showSaveTemplateDialog}
        templateName={templateName}
        setTemplateName={setTemplateName}
        savingTemplate={savingTemplate}
        onCancel={closeSaveTemplateDialog}
        onSave={handleSaveTemplate}
      />

      <LoadJournalTemplatesDialog
        show={showLoadTemplateDialog}
        templates={templates}
        templatesLoading={templatesLoading}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        updatingTemplate={updatingTemplate}
        onClose={closeLoadTemplateDialog}
        onLoad={handleLoadSelectedTemplate}
        onEdit={openEditSelectedTemplate}
        onDelete={handleDeleteSelectedTemplate}
      />

      <EditJournalTemplateDialog
        show={showEditTemplateDialog}
        editingTemplateName={editingTemplateName}
        setEditingTemplateName={setEditingTemplateName}
        updatingTemplate={updatingTemplate}
        onCancel={closeEditTemplateDialog}
        onSave={handleUpdateTemplateName}
      />
    </>
  );
};

export default JournalPostings;
